import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { sessionData } from "../helpers/session-data";
import { myProfileService } from "../services/myProfile.service";
import { teamMemberProfileService } from "../services/teamMemberProfile.service";
import type {
  MyProfilePhoneItem,
  MyProfileAddressItem,
  MyProfileIdProof,
  TermsItem,
  LoginActivityItem,
} from "../components/MainContent/pages/MyProfile/types";
import type { User } from "../components/MainContent/pages/Users/types";

export type UserDetailTabFetchId = "details" | "documents" | "login-activity" | "terms";

export interface UserDetailState {
  userId: string | null;
  member: User | null;
  phoneNumbers: MyProfilePhoneItem[];
  addresses: MyProfileAddressItem[];
  idProofs: MyProfileIdProof[];
  terms: TermsItem[];
  loginActivity: LoginActivityItem[];
  isLoading: boolean;
  isRefreshing: boolean;
  tabLoading: Partial<Record<UserDetailTabFetchId, boolean>>;
  error: string | null;
  tabErrors: Partial<Record<UserDetailTabFetchId, string | null>>;
}

const initialState: UserDetailState = {
  userId: null,
  member: null,
  phoneNumbers: [],
  addresses: [],
  idProofs: [],
  terms: [],
  loginActivity: [],
  isLoading: false,
  isRefreshing: false,
  tabLoading: {},
  error: null,
  tabErrors: {},
};

function errorMessageFromApi(errors: unknown, fallback: string): string {
  if (typeof errors === "string" && errors.trim()) return errors.trim();
  if (Array.isArray(errors)) {
    const parts = errors.map((e) => String(e).trim()).filter(Boolean);
    if (parts.length > 0) return parts.join(". ");
  }
  return fallback;
}

type FetchMemberArg = string | { userId: string; isRefresh?: boolean };

function normalizeMemberArg(arg: FetchMemberArg): { userId: string; isRefresh: boolean } {
  if (typeof arg === "string") return { userId: arg.trim(), isRefresh: false };
  return { userId: arg.userId.trim(), isRefresh: arg.isRefresh === true };
}

function getSessionPayload(): {
  business_id: string;
  device_token: string;
  session_id: string;
} | null {
  const { business_id, device_token, session_id } = sessionData();
  if (!business_id || !device_token || !session_id) return null;
  return { business_id, device_token, session_id };
}

export const fetchUserDetailMember = createAsyncThunk(
  "userDetail/fetchMember",
  async (arg: FetchMemberArg, { rejectWithValue }) => {
    const { userId, isRefresh } = normalizeMemberArg(arg);
    if (!userId) return rejectWithValue("User id required");

    const session = getSessionPayload();
    if (!session) return rejectWithValue("Session not available");

    const profileArgs = { ...session, user_id: userId };
    const detailRes = await teamMemberProfileService.getTeamMemberDetail(profileArgs);
    const userDetails = detailRes.data?.user_details;
    if (detailRes.success !== true || !userDetails) {
      return rejectWithValue(
        errorMessageFromApi(detailRes.errors, detailRes.message ?? "Failed to load user details")
      );
    }

    return {
      userId,
      isRefresh,
      member: teamMemberProfileService.mapTeamMemberDetailToUser(userDetails),
      loginActivity: teamMemberProfileService.loginActivityFromTeamMemberDetail(userDetails),
    };
  }
);

type FetchTabArg = {
  userId: string;
  tab: UserDetailTabFetchId;
  isRefresh?: boolean;
};

export const fetchUserDetailTab = createAsyncThunk(
  "userDetail/fetchTab",
  async ({ userId, tab, isRefresh = false }: FetchTabArg, { rejectWithValue }) => {
    if (!userId.trim()) return rejectWithValue("User id required");

    const session = getSessionPayload();
    if (!session) return rejectWithValue("Session not available");

    const profileArgs = { ...session, user_id: userId };

    switch (tab) {
      case "details": {
        const [phoneNumbers, addresses] = await Promise.all([
          teamMemberProfileService.getTeamMemberPhoneNumbers(profileArgs),
          teamMemberProfileService.getTeamMemberAddresses(profileArgs),
        ]);
        return { userId, tab, isRefresh, phoneNumbers, addresses };
      }
      case "documents": {
        const idProofs = await teamMemberProfileService.getTeamMemberProofs(profileArgs);
        return { userId, tab, isRefresh, idProofs };
      }
      case "login-activity": {
        const detailRes = await teamMemberProfileService.getTeamMemberDetail(profileArgs);
        const userDetails = detailRes.data?.user_details;
        if (detailRes.success !== true || !userDetails) {
          return rejectWithValue(
            errorMessageFromApi(
              detailRes.errors,
              detailRes.message ?? "Failed to load login activity"
            )
          );
        }
        return {
          userId,
          tab,
          isRefresh,
          loginActivity: teamMemberProfileService.loginActivityFromTeamMemberDetail(userDetails),
          member: teamMemberProfileService.mapTeamMemberDetailToUser(userDetails),
        };
      }
      case "terms": {
        let terms: TermsItem[] = [];
        try {
          terms =
            (await myProfileService.getTermsStatusCheck(session, { forUserId: userId })) ?? [];
        } catch {
          terms = [];
        }
        if (terms.length === 0) {
          try {
            terms = (await myProfileService.getTermsStatusCheck(session)) ?? [];
          } catch {
            terms = [];
          }
        }
        return { userId, tab, isRefresh, terms };
      }
      default:
        return rejectWithValue("Unknown tab");
    }
  }
);

const userDetailSlice = createSlice({
  name: "userDetail",
  initialState,
  reducers: {
    clearUserDetailError: (state) => {
      state.error = null;
    },
    clearUserDetailTabError: (state, action: PayloadAction<UserDetailTabFetchId>) => {
      state.tabErrors[action.payload] = null;
    },
    clearUserDetail: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailMember.pending, (state, action) => {
        const { userId, isRefresh } = normalizeMemberArg(action.meta.arg as FetchMemberArg);
        if (state.userId !== userId) {
          state.member = null;
          state.phoneNumbers = [];
          state.addresses = [];
          state.idProofs = [];
          state.terms = [];
          state.loginActivity = [];
          state.tabLoading = {};
          state.tabErrors = {};
        }
        state.userId = userId;
        state.error = null;
        state.isRefreshing = isRefresh;
        state.isLoading = !isRefresh;
      })
      .addCase(fetchUserDetailMember.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.userId = action.payload.userId;
        state.member = action.payload.member;
        state.loginActivity = action.payload.loginActivity;
        state.error = null;
        if (state.idProofs.length > 0) {
          state.idProofs = teamMemberProfileService.enrichTeamMemberIdProofs(
            state.idProofs,
            state.member
          );
        }
      })
      .addCase(fetchUserDetailMember.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        const isRefresh = normalizeMemberArg(action.meta.arg as FetchMemberArg).isRefresh;
        if (!isRefresh) {
          state.member = null;
          state.loginActivity = [];
        }
        state.error = (action.payload as string) ?? action.error.message ?? "Failed to load user";
      })
      .addCase(fetchUserDetailTab.pending, (state, action) => {
        const { tab, userId } = action.meta.arg as FetchTabArg;
        if (state.userId !== userId) {
          state.userId = userId;
        }
        state.tabLoading[tab] = true;
        state.tabErrors[tab] = null;
      })
      .addCase(fetchUserDetailTab.fulfilled, (state, action) => {
        const { tab, userId } = action.payload;
        if (state.userId !== userId) return;
        state.tabLoading[tab] = false;
        state.tabErrors[tab] = null;

        switch (tab) {
          case "details":
            state.phoneNumbers = action.payload.phoneNumbers ?? [];
            state.addresses = action.payload.addresses ?? [];
            break;
          case "documents":
            state.idProofs = teamMemberProfileService.enrichTeamMemberIdProofs(
              action.payload.idProofs ?? [],
              state.member
            );
            break;
          case "login-activity":
            state.loginActivity = action.payload.loginActivity ?? [];
            if (action.payload.member) state.member = action.payload.member;
            break;
          case "terms":
            state.terms = action.payload.terms ?? [];
            break;
          default:
            break;
        }
      })
      .addCase(fetchUserDetailTab.rejected, (state, action) => {
        const tab = (action.meta.arg as FetchTabArg).tab;
        state.tabLoading[tab] = false;
        state.tabErrors[tab] =
          (action.payload as string) ?? action.error.message ?? "Failed to load tab data";
      });
  },
});

export const { clearUserDetailError, clearUserDetailTabError, clearUserDetail } =
  userDetailSlice.actions;

export const selectUserDetailMember = (state: { userDetail?: UserDetailState }): User | null =>
  state.userDetail?.member ?? null;

export const selectUserDetailPhoneNumbers = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.phoneNumbers ?? [];

export const selectUserDetailAddresses = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.addresses ?? [];

export const selectUserDetailIdProofs = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.idProofs ?? [];

export const selectUserDetailTerms = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.terms ?? [];

export const selectUserDetailLoginActivity = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.loginActivity ?? [];

export const selectUserDetailLoading = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.isLoading ?? false;

export const selectUserDetailRefreshing = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.isRefreshing ?? false;

export const selectUserDetailError = (state: { userDetail?: UserDetailState }) =>
  state.userDetail?.error ?? null;

export const selectUserDetailTabLoading =
  (tab: UserDetailTabFetchId) =>
  (state: { userDetail?: UserDetailState }): boolean =>
    state.userDetail?.tabLoading[tab] ?? false;

export const selectUserDetailTabError =
  (tab: UserDetailTabFetchId) =>
  (state: { userDetail?: UserDetailState }): string | null =>
    state.userDetail?.tabErrors[tab] ?? null;

export default userDetailSlice.reducer;
